import {
  Alert,
  BOOLEAN_SELECT_LIST_ITEMS,
  BULK_EDIT_UPDATE_EMPTY_WARNING,
  Card,
  Checkbox,
  Grid,
  pn,
  Stack,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import {
  LkStudentDisplayFieldId,
  StudentBulkUpdate,
  StudentBulkUpdateResource,
  StudentBulkUpdateSchema,
} from "generated/openApi";

export const StudentUpdateSchema = (props: {
  updateSchema: StudentBulkUpdateSchema;
  setUpdateSchema: (value: StudentBulkUpdateSchema) => void;
  resource: StudentBulkUpdateResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties =
    props.resource.properties?.find((x) => x.propertyName === pn<StudentBulkUpdate>("updateSchema"))?.children || [];
  const displayFields = props.resource.displayFields || [];

  const assignedUserDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AssignedUser);
  const feederSchoolDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FeederSchool);
  const enrollmentDateDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.EnrollmentDate);
  const usCitizenDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.UsCitizen);
  const atRiskStdTestDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskStdTest);
  const atRiskLowGpaDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskLowGpa);
  const atRiskAlgebraDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskAlgebra);
  const otherNeedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.OtherNeed);
  const disconnectedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Disconnected);
  const ninthGradeYearDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.NinthGradeYear);
  const permissionSlipDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PermissionSlip);
  const collegeBoundDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.CollegeBound);
  const mentorProgramDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.MentorProgram);
  const migrantDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Migrant);
  const sixYearsEslDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.SixYearsEsl);
  const dualEnrollmentDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.DualEnrollment);
  const povertyDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Poverty);
  const referralTypeDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.ReferralType);
  const primaryTransportationDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PrimaryTransportation);
  const reasonForEnrollmentDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.ReasonForEnrollment);
  const graduationYearDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.GraduationYear);
  const familyIncomeDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FamilyIncome);
  const cohortDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Cohort);
  const eligibilityDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Eligibility);
  const academicNeedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AcademicNeed);
  const firstGenerationDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FirstGeneration);
  const noCollegeReasonDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.NoCollegeReason);



  const genderField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("genderId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateGenderId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateGenderId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.genders?.find((x) => x.id === props.updateSchema.genderId)}
          options={props.resource.genders}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, genderId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const cohortField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("cohortId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCohortId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCohortId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={cohortDisplayField?.displayName}
          required={cohortDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.cohorts?.find((x) => x.id === props.updateSchema.cohortId)}
          options={props.resource.cohorts}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, cohortId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const permissionSlipField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("permissionSlip");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePermissionSlip;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updatePermissionSlip: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={permissionSlipDisplayField?.displayName}
          required={permissionSlipDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.permissionSlip)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, permissionSlip: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const collegeBoundField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("collegeBound");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCollegeBound;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCollegeBound: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={collegeBoundDisplayField?.displayName}
          required={collegeBoundDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.collegeBound)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, collegeBound: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const mentorProgramField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("mentorProgram");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateMentorProgram;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateMentorProgram: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={mentorProgramDisplayField?.displayName}
          required={mentorProgramDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.mentorProgram)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, mentorProgram: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const assignedUserField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("assignedUserId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAssignedUserId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAssignedUserId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={assignedUserDisplayField?.displayName}
          required={assignedUserDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.users?.find((x) => x.id === props.updateSchema.assignedUserId)}
          options={props.resource.users}
          getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, assignedUserId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const raceIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("raceId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateRaceId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateRaceId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.races?.find((x) => x.id === props.updateSchema.raceId)}
          options={props.resource.races}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, raceId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const ethnicityIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("ethnicityId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEthnicityId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEthnicityId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.ethnicities?.find((x) => x.id === props.updateSchema.ethnicityId)}
          options={props.resource.ethnicities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, ethnicityId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const enrollmentDateField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("enrollmentDate");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEnrollmentDate;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEnrollmentDate: e.target.checked })}
        />
        <DatePicker
          modelProperty={property}
          label={enrollmentDateDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.enrollmentDate}
          onChange={(date) => props.setUpdateSchema({ ...props.updateSchema, enrollmentDate: date })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const feederSchoolIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("feederSchoolId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFeederSchoolId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFeederSchoolId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={feederSchoolDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.feederSchools?.find((x) => x.id === props.updateSchema.feederSchoolId)}
          options={props.resource.feederSchools}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, feederSchoolId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const graduationYearField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("graduationYear");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateGraduationYear;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateGraduationYear: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={graduationYearDisplayField?.displayName}
          required={graduationYearDisplayField?.required}
          type="number"
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.graduationYear}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, graduationYear: e.target.value as unknown as number })
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const migrantField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("migrant");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateMigrant;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateMigrant: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={migrantDisplayField?.displayName}
          required={migrantDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.migrant)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, migrant: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const sixYearsEslField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("sixYearsEsl");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateSixYearsEsl;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateSixYearsEsl: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={sixYearsEslDisplayField?.displayName}
          required={sixYearsEslDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.sixYearsEsl)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, sixYearsEsl: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const dualEnrollmentField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("dualEnrollment");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDualEnrollment;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDualEnrollment: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={dualEnrollmentDisplayField?.displayName}
          required={dualEnrollmentDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.dualEnrollment)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, dualEnrollment: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const povertyField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("poverty");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePoverty;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updatePoverty: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={povertyDisplayField?.displayName}
          required={povertyDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.poverty)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, poverty: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const firstGenerationField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("firstGeneration");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFirstGeneration;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFirstGeneration: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={firstGenerationDisplayField?.displayName}
          required={firstGenerationDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.firstGeneration)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, firstGeneration: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const ninthGradeYearField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("ninthGradeYear");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateNinthGradeYear;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateNinthGradeYear: e.target.checked })}
        />
        <Input
          modelProperty={property}
          label={ninthGradeYearDisplayField?.displayName}
          required={ninthGradeYearDisplayField?.required}
          type="number"
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.ninthGradeYear}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, ninthGradeYear: e.target.value as unknown as number })
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const referralTypeIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("referralTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateReferralTypeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateReferralTypeId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={referralTypeDisplayField?.displayName}
          required={referralTypeDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.referralTypes?.find((x) => x.id === props.updateSchema.referralTypeId)}
          options={props.resource.referralTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, referralTypeId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const noCollegeReasonIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("noCollegeReasonId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateNoCollegeReasonId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateNoCollegeReasonId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={noCollegeReasonDisplayField?.displayName}
          required={noCollegeReasonDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.noCollegeReasons?.find((x) => x.id === props.updateSchema.noCollegeReasonId)}
          options={props.resource.noCollegeReasons}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, noCollegeReasonId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const reasonForEnrollmentIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("reasonForEnrollmentId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateReasonForEnrollmentId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updateReasonForEnrollmentId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          label={reasonForEnrollmentDisplayField?.displayName}
          required={reasonForEnrollmentDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.reasonsForEnrollment?.find((x) => x.id === props.updateSchema.reasonForEnrollmentId)}
          options={props.resource.reasonsForEnrollment}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, reasonForEnrollmentId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const primaryTransportationIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("primaryTransportationId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePrimaryTransportationId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) =>
            props.setUpdateSchema({ ...props.updateSchema, updatePrimaryTransportationId: e.target.checked })
          }
        />
        <Select
          modelProperty={property}
          label={primaryTransportationDisplayField?.displayName}
          required={primaryTransportationDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.primaryTransportations?.find(
            (x) => x.id === props.updateSchema.primaryTransportationId
          )}
          options={props.resource.primaryTransportations}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, primaryTransportationId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const eligibilityIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("eligibilityId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateEligibilityId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateEligibilityId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={eligibilityDisplayField?.displayName}
          required={eligibilityDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.eligibilities?.find((x) => x.id === props.updateSchema.eligibilityId)}
          options={props.resource.eligibilities}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, eligibilityId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const academicNeedIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("academicNeedId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAcademicNeedId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAcademicNeedId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={academicNeedDisplayField?.displayName}
          required={academicNeedDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.academicNeeds?.find((x) => x.id === props.updateSchema.academicNeedId)}
          options={props.resource.academicNeeds}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, academicNeedId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const familyIncomeIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("familyIncomeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateFamilyIncomeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateFamilyIncomeId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={familyIncomeDisplayField?.displayName}
          required={familyIncomeDisplayField?.required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.familyIncomes?.find((x) => x.id === props.updateSchema.familyIncomeId)}
          options={props.resource.familyIncomes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, familyIncomeId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const stateField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("state");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateState;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateState: e.target.checked })}
        />
        <Input
          modelProperty={property}
          value={props.updateSchema.state}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, state: e.target.value })}
          errors={props.validationErrors[propertyName]}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const cityField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("city");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateCity;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateCity: e.target.checked })}
        />
        <Input
          modelProperty={property}
          value={props.updateSchema.city}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, city: e.target.value })}
          errors={props.validationErrors[propertyName]}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const postal = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("postal");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updatePostal;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updatePostal: e.target.checked })}
        />
        <Input
          modelProperty={property}
          value={props.updateSchema.postal}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, postal: e.target.value })}
          errors={props.validationErrors[propertyName]}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const usCitizenField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("usCitizen");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateUsCitizen;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateUsCitizen: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={usCitizenDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.usCitizen)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, usCitizen: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const atRiskStdTestIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("atRiskStdTestId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAtRiskStdTestId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAtRiskStdTestId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={atRiskStdTestDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.atRiskStdTests?.find((x) => x.id === props.updateSchema.atRiskStdTestId)}
          options={props.resource.atRiskStdTests}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, atRiskStdTestId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const atRiskLowGpaIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("atRiskLowGpaId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAtRiskLowGpaId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAtRiskLowGpaId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={atRiskLowGpaDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.atRiskLowGpas?.find((x) => x.id === props.updateSchema.atRiskLowGpaId)}
          options={props.resource.atRiskLowGpas}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, atRiskLowGpaId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const atRiskAlgebraIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("atRiskAlgebraId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateAtRiskAlgebraId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateAtRiskAlgebraId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={atRiskAlgebraDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.atRiskAlgebras?.find((x) => x.id === props.updateSchema.atRiskAlgebraId)}
          options={props.resource.atRiskAlgebras}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, atRiskAlgebraId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const otherNeedIdField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("otherNeedId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateOtherNeedId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateOtherNeedId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={otherNeedDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={props.resource.otherNeeds?.find((x) => x.id === props.updateSchema.otherNeedId)}
          options={props.resource.otherNeeds}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, otherNeedId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const disconnectedField = () => {
    const propertyName = pn<StudentBulkUpdateSchema>("disconnected");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDisconnected;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDisconnected: e.target.checked })}
        />
        <Select
          modelProperty={property}
          label={disconnectedDisplayField?.displayName}
          errors={props.validationErrors[propertyName]}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.updateSchema.disconnected)}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, disconnected: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  return (
    <Card header={"Updating Fields"}>
      <Stack direction="column" spacing="lg">
        <Alert severity="warning" variant="standard">
          {BULK_EDIT_UPDATE_EMPTY_WARNING}
        </Alert>

        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {genderField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {ethnicityIdField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {raceIdField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {stateField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {cityField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {postal()}
          </Grid>

          {enrollmentDateDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {enrollmentDateField()}
            </Grid>
          )}

          {feederSchoolDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {feederSchoolIdField()}
            </Grid>
          )}

          {graduationYearDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {graduationYearField()}
            </Grid>
          )}

          {cohortDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {cohortField()}
            </Grid>
          )}

          {permissionSlipDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {permissionSlipField()}
            </Grid>
          )}

          {collegeBoundDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {collegeBoundField()}
            </Grid>
          )}

          {mentorProgramDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {mentorProgramField()}
            </Grid>
          )}

          {migrantDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {migrantField()}
            </Grid>
          )}

          {sixYearsEslDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {sixYearsEslField()}
            </Grid>
          )}

          {dualEnrollmentDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentField()}
            </Grid>
          )}

          {povertyDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {povertyField()}
            </Grid>
          )}

          {firstGenerationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {firstGenerationField()}
            </Grid>
          )}

          {ninthGradeYearDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {ninthGradeYearField()}
            </Grid>
          )}

          {referralTypeDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {referralTypeIdField()}
            </Grid>
          )}

          {noCollegeReasonDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {noCollegeReasonIdField()}
            </Grid>
          )}

          {reasonForEnrollmentDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {reasonForEnrollmentIdField()}
            </Grid>
          )}

          {primaryTransportationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {primaryTransportationIdField()}
            </Grid>
          )}

          {eligibilityDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {eligibilityIdField()}
            </Grid>
          )}

          {atRiskStdTestDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {atRiskStdTestIdField()}
            </Grid>
          )}

          {atRiskLowGpaDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {atRiskLowGpaIdField()}
            </Grid>
          )}

          {atRiskAlgebraDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {atRiskAlgebraIdField()}
            </Grid>
          )}

          {disconnectedDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {disconnectedField()}
            </Grid>
          )}

          {otherNeedDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {otherNeedIdField()}
            </Grid>
          )}

          {usCitizenDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {usCitizenField()}
            </Grid>
          )}

          {academicNeedDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {academicNeedIdField()}
            </Grid>
          )}

          {familyIncomeDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {familyIncomeIdField()}
            </Grid>
          )}

          {assignedUserDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {assignedUserField()}
            </Grid>
          )}
        </Grid>
      </Stack>
    </Card>
  );
};
